import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import useDensity, { DEFAULT_DENSITY, DENSITIES } from '../hooks/useDensity';

/**
 * Appearance (visual theme) and color scheme selection.
 *
 * Applies `data-appearance` and `data-scheme` to <html>, which swap the
 * custom-property blocks in styles/appearance.css and re-skin every Tailwind
 * color/radius/shadow/font at once. See that file for why the values are RGB
 * channels. Forest has a light and a dark scheme; Fable ignores data-scheme.
 *
 * Both are stored per USER, not per device: the shop's terminals are shared,
 * so keying on the device alone would mean one staff member's choice follows
 * whoever logs in next. Falls back to a device-level key before a user is known
 * (and for the login screen), so the choice survives a logout instead of
 * snapping back.
 *
 * Density (counter / bench) is the exception — it is per device, owned by
 * useDensity, and only mounted here so it runs app-wide.
 *
 * NOTE: this is still browser-local. A staff member who moves to a different
 * terminal starts from the default again — making it follow them everywhere
 * needs a preference column on the user record.
 */

export const APPEARANCES = [
  {
    id: 'forest',
    name: 'Forest',
    description: 'Forest green on a clean, flat ground — the default look.',
  },
  {
    id: 'fable',
    name: 'Fable',
    description: 'Terracotta and sage on cream, with a serif display face.',
  },
];

export const DEFAULT_APPEARANCE = 'forest';

// Forest was stored as `rosa` before the Forest overhaul. Read as an alias and
// rewritten in place, so existing staff keep their choice.
const LEGACY_APPEARANCE_IDS = { rosa: 'forest' };
const normalizeAppearance = (value) => LEGACY_APPEARANCE_IDS[value] ?? value;
const isValidAppearance = (value) => APPEARANCES.some((a) => a.id === value);

export const SCHEMES = ['light', 'dark', 'system'];
// Anyone who has never chosen follows their device's light/dark setting live.
// An explicit stored choice (light/dark/system) or ?scheme= wins.
export const DEFAULT_SCHEME = 'system';
const isValidScheme = (value) => SCHEMES.includes(value);

const APPEARANCE_KEY = 'twigs_appearance';
const SCHEME_KEY = 'twigs_scheme';
const keyFor = (base, userId) => (userId ? `${base}:${userId}` : base);

const identity = (value) => value;

/**
 * Resolve a stored preference for a given user.
 *
 * Once a user is known, their key is AUTHORITATIVE and the device key is not
 * consulted — otherwise the first person to pick Fable on a shared terminal
 * silently hands their theme to everyone who signs in after them.
 *
 * The device key is only a first-paint hint (see the bootstrap script in
 * index.html) and the pre-login default. Trade-off: a second staff member can
 * see one frame of the previous user's theme before this corrects it.
 *
 * A legacy value (`rosa`) is normalised and written back under the same key.
 */
const readStored = (base, userId, { isValid, fallback, normalize = identity }) => {
  try {
    const key = keyFor(base, userId);
    const raw = localStorage.getItem(key);
    const value = normalize(raw);
    if (isValid(value)) {
      if (value !== raw) localStorage.setItem(key, value);
      return value;
    }
  } catch {
    // Private-mode / disabled storage — fall through to the default.
  }
  return fallback;
};

const writeStored = (base, userId, value) => {
  try {
    // Device key is the first-paint hint + pre-login default, never the
    // source of truth for a signed-in user (see readStored).
    localStorage.setItem(base, value);
    if (userId) localStorage.setItem(keyFor(base, userId), value);
  } catch {
    // Non-persistent is still better than not switching at all.
  }
};

/**
 * `?appearance=fable` / `?scheme=dark` override and persist the choice.
 *
 * Kept in for review and support: it makes any theme reachable on a device
 * that cannot sign in (a tablet on the shop floor, a review build with no
 * backend), and lets support say "open this link" instead of walking someone
 * through Profile over the phone. `?appearance=rosa` still means Forest.
 */
const readQueryOverride = (param, { isValid, normalize = identity }) => {
  try {
    const value = normalize(new URLSearchParams(window.location.search).get(param));
    return isValid(value) ? value : null;
  } catch {
    return null;
  }
};

const APPEARANCE_OPTS = {
  isValid: isValidAppearance,
  fallback: DEFAULT_APPEARANCE,
  normalize: normalizeAppearance,
};
const SCHEME_OPTS = { isValid: isValidScheme, fallback: DEFAULT_SCHEME };

const DARK_QUERY = '(prefers-color-scheme: dark)';

const systemPrefersDark = () => {
  try {
    return Boolean(window.matchMedia && window.matchMedia(DARK_QUERY).matches);
  } catch {
    return false;
  }
};

/** `system` follows the OS live; light and dark are fixed. */
const useResolvedScheme = (scheme) => {
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (scheme !== 'system' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (e) => setSystemDark(e.matches);
    setSystemDark(mql.matches);
    // Safari < 14 only has the deprecated addListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [scheme]);

  if (scheme === 'system') return systemDark ? 'dark' : 'light';
  return scheme;
};

const AppearanceContext = createContext({
  // Only the no-provider default carries this, so screens rendered outside
  // AppearanceProvider (e.g. /signup) can tell and read <html> instead.
  outsideProvider: true,
  appearance: DEFAULT_APPEARANCE,
  setAppearance: () => {},
  appearances: APPEARANCES,
  scheme: DEFAULT_SCHEME,
  setScheme: () => {},
  resolvedScheme: 'light',
  schemes: SCHEMES,
  density: DEFAULT_DENSITY,
  setDensity: () => {},
  densities: DENSITIES,
});

export const useAppearance = () => useContext(AppearanceContext);

export const AppearanceProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [appearance, setAppearanceState] = useState(
    () => readQueryOverride('appearance', APPEARANCE_OPTS) ?? readStored(APPEARANCE_KEY, null, APPEARANCE_OPTS)
  );
  const [scheme, setSchemeState] = useState(
    () => readQueryOverride('scheme', SCHEME_OPTS) ?? readStored(SCHEME_KEY, null, SCHEME_OPTS)
  );
  const resolvedScheme = useResolvedScheme(scheme);
  const { density, setDensity, densities } = useDensity();

  const setAppearance = useCallback(
    (next) => {
      const value = normalizeAppearance(next);
      if (!isValidAppearance(value)) return;
      setAppearanceState(value);
      writeStored(APPEARANCE_KEY, userId, value);
    },
    [userId]
  );

  const setScheme = useCallback(
    (next) => {
      if (!isValidScheme(next)) return;
      setSchemeState(next);
      writeStored(SCHEME_KEY, userId, next);
    },
    [userId]
  );

  // Re-resolve when the signed-in user changes, so logging in picks up that
  // person's choice rather than leaving the previous user's theme on screen.
  //
  // An explicit ?appearance= / ?scheme= wins and is PERSISTED for whoever is now
  // signed in. Persisting matters: without it the override survives only until
  // login, and "open this link to see Fable" would snap back to the default the
  // moment you authenticate — which is exactly when you start wanting to look.
  useEffect(() => {
    const override = readQueryOverride('appearance', APPEARANCE_OPTS);
    if (override) {
      setAppearance(override);
    } else {
      setAppearanceState(readStored(APPEARANCE_KEY, userId, APPEARANCE_OPTS));
    }
  }, [userId, setAppearance]);

  useEffect(() => {
    const override = readQueryOverride('scheme', SCHEME_OPTS);
    if (override) {
      setScheme(override);
    } else {
      setSchemeState(readStored(SCHEME_KEY, userId, SCHEME_OPTS));
    }
  }, [userId, setScheme]);

  // The lines that actually re-skin the app.
  useEffect(() => {
    document.documentElement.setAttribute('data-appearance', appearance);
  }, [appearance]);

  useEffect(() => {
    document.documentElement.setAttribute('data-scheme', resolvedScheme);
  }, [resolvedScheme]);

  return (
    <AppearanceContext.Provider
      value={{
        appearance,
        setAppearance,
        appearances: APPEARANCES,
        scheme,
        setScheme,
        resolvedScheme,
        schemes: SCHEMES,
        density,
        setDensity,
        densities,
      }}
    >
      {children}
    </AppearanceContext.Provider>
  );
};

export default AppearanceContext;
